import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPaymentRunCandidates, createPaymentRun, getPaymentRuns, getPaymentRun, downloadCsv, money } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Landmark, Download, Loader2, CheckSquare, Square, AlertTriangle, FileSpreadsheet, History, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const METHODS = [["bank_transfer", "Bank transfer"], ["card", "Card"], ["cheque", "Cheque"], ["other", "Other"]];
const today = () => new Date().toISOString().slice(0, 10);

const Stat = ({ label, value, tone }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={cn("font-mono text-lg font-semibold", tone)}>{value}</div>
  </div>
);

export default function PaymentRuns() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [paidAt, setPaidAt] = useState(today());
  const [method, setMethod] = useState("bank_transfer");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, r] = await Promise.all([getPaymentRunCandidates(), getPaymentRuns()]);
    setData(c);
    setRuns(r);
    setSelected(new Set(c.items.map((i) => i.id)));
  };
  useEffect(() => { load(); }, []);

  const chosen = useMemo(() => (data ? data.items.filter((i) => selected.has(i.id)) : []), [data, selected]);
  const chosenTotal = chosen.reduce((a, i) => a + i.total, 0);
  const missingEmail = chosen.filter((i) => !i.payee_email).length;
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const create = async () => {
    setBusy(true);
    try {
      const run = await createPaymentRun({ ids: [...selected], paid_at: paidAt, method, user });
      downloadCsv(run.filename, run.content);
      toast.success(`Payment run ${run.run_number} created — ${run.invoice_count} invoice(s), ${money(run.total)} marked paid`);
      setConfirming(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create payment run");
    } finally {
      setBusy(false);
    }
  };

  const redownload = async (id) => {
    const run = await getPaymentRun(id);
    downloadCsv(run.filename, run.content);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Payment Runs</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Bundle approved, unpaid invoices into one bank-upload file. Confirming a run marks every invoice as paid and notifies suppliers.
        </p>
      </div>

      {!data ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-xl border border-border bg-card overflow-hidden" data-testid="run-candidates">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-border">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Landmark className="h-4 w-4 text-emerald-500" /> Ready to pay
                <span className="font-mono text-xs text-muted-foreground" data-testid="candidate-count">{data.items.length} invoice(s)</span>
              </div>
              <div className="flex gap-2 text-xs">
                <button data-testid="run-select-all" onClick={() => setSelected(new Set(data.items.map((i) => i.id)))} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-muted-foreground hover:text-foreground"><CheckSquare className="h-3.5 w-3.5" /> All</button>
                <button data-testid="run-select-none" onClick={() => setSelected(new Set())} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-muted-foreground hover:text-foreground"><Square className="h-3.5 w-3.5" /> None</button>
              </div>
            </div>
            {data.items.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-muted-foreground" data-testid="run-empty">Nothing waiting — every approved invoice is already paid.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.items.map((i) => {
                  const overdue = i.due_date && i.due_date < today();
                  return (
                    <label key={i.id} data-testid={`run-item-${i.id}`} className={cn("flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-muted/40", selected.has(i.id) && "bg-primary/5")}>
                      <Checkbox data-testid={`run-check-${i.id}`} checked={selected.has(i.id)} onCheckedChange={() => toggle(i.id)} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{i.supplier}</div>
                        <div className="text-xs text-muted-foreground font-mono flex flex-wrap gap-x-2">
                          <span>{i.invoice_number}</span>
                          <span className={overdue ? "text-red-500" : ""}>due {i.due_date || "—"}</span>
                          {i.payee_email ? <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{i.payee_email}</span> : <span className="text-amber-500">no payee email</span>}
                        </div>
                      </div>
                      <button onClick={(e) => { e.preventDefault(); navigate(`/invoice/${i.id}`); }} className="font-mono text-sm font-semibold hover:text-primary">{money(i.total, i.currency)}</button>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="run-builder">
              <div className="grid grid-cols-2 gap-4">
                <Stat label="Selected" value={chosen.length} />
                <Stat label="Run total" value={money(chosenTotal)} tone="text-emerald-500" />
                <Stat label="Suppliers" value={new Set(chosen.map((c) => c.supplier.toLowerCase())).size} />
                <Stat label="Overdue" value={chosen.filter((c) => c.due_date && c.due_date < today()).length} tone="text-red-500" />
              </div>
              <div className="grid gap-2">
                <label className="text-xs text-muted-foreground">Payment date</label>
                <Input data-testid="run-paid-at" type="date" value={paidAt} max={today()} onChange={(e) => setPaidAt(e.target.value)} className="h-9 font-mono text-sm" />
                <label className="text-xs text-muted-foreground">Method</label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="run-method" className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {missingEmail > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-amber-500" data-testid="run-missing-email">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {missingEmail} supplier(s) have no email on file — they won't receive a "paid" notice. Add emails on the Suppliers page.
                </p>
              )}
              <Button data-testid="create-run-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={chosen.length === 0 || !paidAt} onClick={() => setConfirming(true)}>
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Create run & download file
              </Button>
              <p className="text-[11px] text-muted-foreground">Universal CSV: Payee, Amount, Currency, Payment Reference, Invoice No., Dates, Payee Email. Upload it to your bank's bulk-payment screen.</p>
            </div>

            <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="run-history">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-border text-sm font-medium"><History className="h-4 w-4" /> Past runs <span className="font-mono text-xs text-muted-foreground">{runs.length}</span></div>
              {runs.length === 0 ? (
                <div className="px-5 py-6 text-xs text-muted-foreground text-center">No runs yet.</div>
              ) : (
                <div className="divide-y divide-border max-h-[320px] overflow-auto d20-scroll">
                  {runs.map((r) => (
                    <div key={r.id} data-testid={`run-row-${r.run_number}`} className="flex items-center justify-between gap-3 px-5 py-3">
                      <div className="min-w-0">
                        <div className="font-mono text-sm font-semibold">{r.run_number}</div>
                        <div className="text-xs text-muted-foreground">{r.invoice_count} inv · {money(r.total)} · {r.created_by} · {new Date(r.created_at).toLocaleDateString()}</div>
                      </div>
                      <Button data-testid={`run-download-${r.run_number}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => redownload(r.id)}><Download className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={confirming} onOpenChange={(o) => !o && !busy && setConfirming(false)}>
        <AlertDialogContent data-testid="run-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Create payment run for {chosen.length} invoice(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              {money(chosenTotal)} across {new Set(chosen.map((c) => c.supplier.toLowerCase())).size} supplier(s) will be marked as paid on {paidAt} via {METHODS.find(([k]) => k === method)?.[1]}.
              The bank file downloads immediately, each invoice gets a payment reference, and suppliers with an email on file are notified. Recorded as {user}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button data-testid="confirm-run-btn" onClick={(e) => { e.preventDefault(); create(); }} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Confirm & download
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
